import { useMemo, useState } from 'react';
import { Building2, Loader2, Plus, Search, User, Users } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { useGetContacts } from '@/graphql';
import type { ContactItem } from '@/graphql/queries/contacts/use-get-contacts';
import { useCurrentOrgId } from '@/store/use-my-oganization-store';
import { ContactFormDialog } from './contact-form-dialog';
import type { Contact, ContactDraft, ContactScope } from '../types/contacts.types';

const SCOPE_BADGE: Record<ContactScope, { label: string; icon: typeof User }> = {
  user: { label: 'Personal', icon: User },
  organization: { label: 'Organization', icon: Building2 },
};

interface ContactPickerProps {
  onSelect: (contact: Contact) => void;
  label?: string;
  allowCreate?: boolean;
  canShareWithOrg?: boolean;
  createDefaults?: ContactDraft;
  align?: 'start' | 'center' | 'end';
  triggerClassName?: string;
}

export function ContactPicker({
  onSelect,
  label = 'Use a contact',
  allowCreate = true,
  canShareWithOrg = true,
  createDefaults,
  align = 'start',
  triggerClassName,
}: ContactPickerProps) {
  const orgId = useCurrentOrgId();
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState('');

  const { data, isLoading } = useGetContacts(
    {
      where: { OrganizationId: { eq: orgId } },
      paging: { pageNo: 1, pageSize: 200 },
    },
    { enabled: (open || createOpen) && !!orgId }
  );

  const filtered = useMemo(() => {
    const list = data?.getContactss?.items ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      [c.Name, c.Email, c.Company, c.Role].some((f) => f && f.toLowerCase().includes(q))
    );
  }, [data, query]);

  const toContact = (item: ContactItem): Contact => ({
    id: item.ItemId,
    createdBy: item.CreatedBy ?? undefined,
    scope: item.Scope === 'organization' ? 'organization' : 'user',
    name: item.Name,
    email: item.Email ?? '',
    role: item.Role ?? '',
    company: item.Company ?? '',
    companyId: item.CompanyId ?? '',
    phone: item.Phone ?? '',
    address: item.Address ?? '',
    canManage: true,
  });

  const pick = (c: Contact) => {
    onSelect(c);
    setOpen(false);
    setCreateOpen(false);
  };

  const pickItem = (item: ContactItem) => pick(toContact(item));

  // Close the popover before opening the dialog so their focus traps don't fight.
  const openCreate = () => {
    setOpen(false);
    setCreateOpen(true);
  };

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setQuery('');
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={`gap-1.5 ${triggerClassName ?? ''}`}
          >
            <Users className="h-3.5 w-3.5 text-selise-blue" />
            {label}
          </Button>
        </PopoverTrigger>
        <PopoverContent align={align} className="w-80 p-0">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search className="h-4 w-4 text-globe-grey shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search contacts…"
              aria-label="Search contacts"
              className="w-full bg-transparent text-sm outline-none placeholder:text-globe-grey"
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-globe-grey">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-globe-grey">
                {query ? 'No matching contacts.' : 'No contacts yet.'}
              </p>
            ) : (
              <ul className="py-1">
                {filtered.map((c) => {
                  const scope: ContactScope = c.Scope === 'organization' ? 'organization' : 'user';
                  const badge = SCOPE_BADGE[scope];
                  const BadgeIcon = badge.icon;
                  const secondary = [c.Role, c.Company, c.Email].filter(Boolean).join(' · ');
                  return (
                    <li key={c.ItemId}>
                      <button
                        type="button"
                        onClick={() => pickItem(c)}
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-muted transition-colors"
                      >
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-selise-blue/10 text-selise-blue shrink-0">
                          <BadgeIcon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-[hsl(var(--fg))] truncate">
                              {c.Name}
                            </span>
                            <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-globe-grey">
                              {badge.label}
                            </span>
                          </div>
                          {secondary && (
                            <p className="text-[11px] text-globe-grey truncate">{secondary}</p>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {allowCreate && (
            <button
              type="button"
              onClick={openCreate}
              className="flex w-full items-center gap-2 border-t border-border px-3 py-2.5 text-sm font-medium text-selise-blue hover:bg-muted transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create new contact
            </button>
          )}
        </PopoverContent>
      </Popover>

      {allowCreate && (
        <ContactFormDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          canShareWithOrg={canShareWithOrg}
          defaults={{ ...createDefaults, name: createDefaults?.name || query.trim() || '' }}
          onCreated={pick}
        />
      )}
    </>
  );
}
